import { ParsedUrlQuery } from 'querystring';
import {
  transformSlug,
  convertToNumber,
  convertToBoolean,
  getValueFromObject,
  toQueryString,
  camelToSnakeCase,
  PropertyValue
} from './common';
import searchFormConditionsValues from '../../defaultData/searchFormConditionsValues';
import getCategoriesDefaultData from '../../defaultData/categories';
import {
  SLUG_FILTER_CONFIGS,
  SLUG_FILTER_REGEX,
  LeasedSlugs
} from './slugFilters';
import {
  Category,
  GetDataForTransformationsQuery,
  GetPashaFilterDataQuery,
  ItemBuildingTypeEnum,
  ItemConnectionSort,
  ItemSort,
  LocalDataQuery,
  Location,
  SortOption
} from '../../graphql/graphql';
import {
  FilterState,
  QueryData,
  SearchFormValues,
  TransformationData
} from './types';
import { normalizeSortByCategory } from './sortMapping';

export type { FilterState, SearchFormValues };

const convertRoomsData = (data?: string | string[]) => {
  if (typeof data === 'undefined') {
    return;
  }

  return typeof data === 'object'
    ? data
    : [data.split('-')[0] !== '5' ? data.split('-')[0] : '5+'];
};

const convertLocations = (
  data: string | string[] | undefined,
  dataForTransform: Record<string, PropertyValue>[],
  citySlug: string
) => {
  if (typeof data === 'undefined') {
    return;
  }

  return !(typeof data === 'object')
    ? new Array(
        getValueFromObject<string>(
          dataForTransform,
          `${citySlug}/${data}`,
          'id'
        ) || ''
      )
    : data;
};

const convertBuildingTypes = (
  data?: string | string[]
): ItemBuildingTypeEnum[] | undefined => {
  if (typeof data === 'undefined') {
    return;
  }

  const result = !(typeof data === 'object') ? new Array(data) : data;

  return result.map(i => i.toUpperCase() as ItemBuildingTypeEnum);
};

const createUrlRegExps = (data?: TransformationData | null) => {
  if (!data) {
    return false;
  }

  function getValues(obj: object | null | undefined, key: string): string[] {
    if (!obj) {
      return [];
    }
    const record = obj as Record<string, PropertyValue>;
    const keysArr = Object.keys(record);
    let objects: string[] = [];

    for (let i = 0; i < keysArr.length; i += 1) {
      const val = record[keysArr[i]];
      if (val !== null && typeof val === 'object') {
        objects = objects.concat(getValues(val, key));
      } else if (keysArr[i] === key) {
        objects.push(val as string);
      }
    }

    return objects;
  }

  const dataWithoutLocation = {
    leased: `(${LeasedSlugs.RENT}|${LeasedSlugs.SALE})`,
    category: `(${getValues(data.categories, 'slug').join('|')})`,
    room: '(1-otaqli|2-otaqli|3-otaqli|4-otaqli|5-otaqli)',
    slugFilter: SLUG_FILTER_REGEX
  };

  if (data?.cities) {
    const cityReg = `(${getValues(data.cities, 'slug').join('|')})`;

    return {
      city: cityReg,
      location: `(${getValues(data.locations, 'slug')
        .join('|')
        .replace(new RegExp(`${cityReg}/`, 'g'), '')})`,
      ...dataWithoutLocation
    };
  }

  return dataWithoutLocation;
};

export const locationGroupDispatcher = (
  locationGroupId: string,
  parent: Location['parent'] | null | undefined,
  t: (key: string) => string
) => {
  let groupName: string | null = '';
  if (parent) {
    groupName = parent.fullName ?? null;
  } else {
    switch (locationGroupId) {
      case '1':
        groupName = t('search.locations_search.location_groups.metro');
        break;
      case '2':
        groupName = t('search.locations_search.location_groups.region');
        break;
      case '3':
        groupName = null;
        break;
      case '4':
        groupName = t('search.locations_search.location_groups.landmark');
        break;
      default:
        break;
    }
  }

  return groupName;
};

export const convertValues = (values: SearchFormValues) => ({
  cityId: +values.city_id ? values.city_id : undefined,
  categoryId: values.category_id,
  leased: values.leased === 'true',
  roomIds:
    typeof values.room_ids === 'object' && values.room_ids.length
      ? values.room_ids
      : undefined,
  priceFrom: convertToNumber(values.price_from),
  priceTo: convertToNumber(values.price_to),
  areaFrom: convertToNumber(values.area_from),
  areaTo: convertToNumber(values.area_to),
  landAreaFrom: convertToNumber(values.land_area_from),
  landAreaTo: convertToNumber(values.land_area_to),
  paidDaily: values.paid_daily === 'false' ? undefined : values.paid_daily,
  hasBillOfSale: convertToBoolean(values.has_bill_of_sale),
  hasMortgage: convertToBoolean(values.has_mortgage),
  hasInternalLoan: convertToBoolean(values.has_internal_loan),
  onlyResidences: convertToBoolean(values.only_residences),
  hasRepair:
    values.has_repair && values.has_repair.length
      ? values.has_repair
      : undefined,
  floorFrom: convertToNumber(values.floor_from),
  floorTo: convertToNumber(values.floor_to),
  floorFirst: values.floor_first ? values.floor_first : undefined,
  floorLast: values.floor_last,
  locationIds:
    typeof values.location_ids === 'object' && values.location_ids.length
      ? values.location_ids
      : undefined,
  buildingType:
    typeof values.building_type === 'object' && values.building_type.length
      ? values.building_type
      : undefined
});

const checkConditionValue = <T>(
  conditionValue: PropertyValue,
  is: string[] | Record<string, string[]>,
  value: T
): T | undefined => {
  if (typeof is === 'object' && !!is && !Array.isArray(is)) {
    const isPassCheck = Object.keys(is).every(elem =>
      is[elem].includes((conditionValue as Record<string, string>)[elem])
    );

    return isPassCheck ? value : undefined;
  }
  return is.includes(conditionValue as string) ? value : undefined;
};

export const convertSearchValues = (
  values: SearchFormValues,
  forUrl = false,
  sort?: GetDataForTransformationsQuery['sort']
) => {
  if (forUrl) {
    return {
      city_id: +values.city_id ? values.city_id : undefined,
      category_id: values.category_id,
      leased: values.leased === 'true',
      room_ids: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.room_ids,
        values.room_ids
      ),
      price_from: convertToNumber(values.price_from),
      price_to: convertToNumber(values.price_to),
      area_from: convertToNumber(values.area_from),
      area_to: convertToNumber(values.area_to),
      paid_daily: checkConditionValue(
        values.leased,
        searchFormConditionsValues.paid_daily,
        values.paid_daily &&
          Array.isArray(values.paid_daily) &&
          values.paid_daily.length
          ? values.paid_daily[0] === 'true'
          : undefined
      ),
      land_area_from: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.land_area,
        convertToNumber(values.land_area_from)
      ),
      land_area_to: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.land_area,
        convertToNumber(values.land_area_to)
      ),
      has_bill_of_sale: checkConditionValue(
        values.leased,
        searchFormConditionsValues.has_bill_of_sale,
        convertToBoolean(values.has_bill_of_sale)
      ),
      has_mortgage: checkConditionValue(
        values.leased,
        searchFormConditionsValues.has_mortgage,
        convertToBoolean(values.has_mortgage)
      ),
      only_residences: checkConditionValue(
        values,
        searchFormConditionsValues.only_residences,
        convertToBoolean(values.only_residences)
      ),
      has_repair: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.has_repair,
        values.has_repair && values.has_repair.length
          ? values.has_repair === 'true'
          : undefined
      ),
      has_internal_loan: checkConditionValue(
        values.leased,
        searchFormConditionsValues.has_internal_loan,
        convertToBoolean(values.has_internal_loan)
      ),
      floor_from: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.floors,
        convertToNumber(values.floor_from)
      ),
      floor_to: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.floors,
        convertToNumber(values.floor_to)
      ),
      floor_first: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.floors,
        values.floor_first ? !values.floor_first : undefined
      ),
      floor_last: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.floors,
        values.floor_last ? values.floor_last === 'true' : values.floor_last
      ),
      location_ids: values.location_ids,
      building_type: checkConditionValue(
        values.category_id,
        searchFormConditionsValues.building_type,
        values.building_type
          ? values.building_type.map(type => type.toLowerCase())
          : values.building_type
      ),
      sorting: getValueFromObject(
        sort,
        normalizeSortByCategory(values.category_id, values.sorting),
        'params'
      )
    };
  }
  return {
    cityId: +values.city_id ? values.city_id : undefined,
    categoryId: values.category_id,
    leased: values.leased === 'true',
    roomIds: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.room_ids,
      typeof values.room_ids === 'object' && values.room_ids.length
        ? values.room_ids
        : undefined
    ),
    priceFrom: convertToNumber(values.price_from),
    priceTo: convertToNumber(values.price_to),
    areaFrom: convertToNumber(values.area_from),
    areaTo: convertToNumber(values.area_to),
    paidDaily: checkConditionValue(
      values.leased,
      searchFormConditionsValues.paid_daily,
      values.paid_daily &&
        Array.isArray(values.paid_daily) &&
        values.paid_daily.length
        ? values.paid_daily[0] === 'true'
        : undefined
    ),
    landAreaFrom: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.land_area,
      convertToNumber(values.land_area_from)
    ),
    landAreaTo: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.land_area,
      convertToNumber(values.land_area_to)
    ),
    hasBillOfSale: checkConditionValue(
      values.leased,
      searchFormConditionsValues.has_bill_of_sale,
      convertToBoolean(values.has_bill_of_sale)
    ),
    hasMortgage: checkConditionValue(
      values.leased,
      searchFormConditionsValues.has_bill_of_sale,
      convertToBoolean(values.has_mortgage)
    ),
    hasInternalLoan: checkConditionValue(
      values.leased,
      searchFormConditionsValues.has_internal_loan,
      convertToBoolean(values.has_internal_loan)
    ),
    onlyResidences: checkConditionValue(
      values,
      searchFormConditionsValues.only_residences,
      convertToBoolean(values.only_residences)
    ),
    hasRepair: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.has_repair,
      values.has_repair && values.has_repair.length
        ? values.has_repair === 'true'
        : undefined
    ),
    floorFrom: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.floors,
      convertToNumber(values.floor_from)
    ),
    floorTo: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.floors,
      convertToNumber(values.floor_to)
    ),
    floorFirst: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.floors,
      values.floor_first ? !values.floor_first : undefined
    ),
    floorLast: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.floors,
      values.floor_last ? values.floor_last === 'true' : values.floor_last
    ),
    locationIds:
      typeof values.location_ids === 'object' && values.location_ids.length
        ? values.location_ids
        : undefined,
    buildingType: checkConditionValue(
      values.category_id,
      searchFormConditionsValues.building_type,
      typeof values.building_type === 'object' && values.building_type.length
        ? values.building_type
        : undefined
    )
  };
};

export const fromObjToUrl = (
  values: SearchFormValues,
  {
    city,
    categories,
    rooms,
    sort
  }: {
    city?: LocalDataQuery['city'];
    categories: LocalDataQuery['categories'];
    rooms?: LocalDataQuery['rooms'];
    sort: LocalDataQuery['sort'];
  },
  sortFromUrl?: string | string[],
  isPashaLanding?: boolean,
  itemsView?: string | string[]
) => {
  const editValues = { ...convertSearchValues(values, true, sort) };

  const urlSegmentsList = ['city_id', 'leased', 'category_id'];

  const slugEntries = SLUG_FILTER_CONFIGS.map(config => ({
    config,
    slug: config.filterToSlug(editValues)
  }));

  const activeSlugHandledKeys = new Set(
    slugEntries
      .filter(({ slug }) => slug !== null)
      .flatMap(({ config }) => config.handledKeys)
  );

  const fromDataToUrl = [
    city ? city.slug : '',
    values.location_ids && values.location_ids.length === 1
      ? transformSlug(
          getValueFromObject(
            city ? city.locations : [],
            editValues.location_ids?.[0],
            'slug'
          )
        )
      : undefined,
    values.leased === 'true' ? LeasedSlugs.RENT : LeasedSlugs.SALE,
    getValueFromObject(categories, editValues.category_id, 'slug'),
    values.room_ids &&
    values.room_ids.length === 1 &&
    editValues.room_ids?.length === 1
      ? getValueFromObject(rooms, `${editValues.room_ids?.[0]}`, 'slug')
      : undefined,
    ...slugEntries.map(({ slug }) => slug).filter(Boolean)
  ].filter(el => el != null);

  const queriesData = Object.keys(editValues)
    .map(key => {
      if (key === 'sorting' && !sortFromUrl) {
        editValues[key] = undefined;
      }

      return key;
    })
    .filter(key => {
      if (activeSlugHandledKeys.has(key)) {
        return false;
      }

      if (
        (key === 'room_ids' || key === 'location_ids') &&
        editValues[key] &&
        editValues[key].length <= 1
      ) {
        return false;
      }

      return (
        !urlSegmentsList.includes(key) &&
        editValues[key as keyof typeof editValues] !== undefined
      );
    })
    .reduce<Record<string, PropertyValue>>(
      (obj, key) => ({
        ...obj,
        [key]: editValues[key as keyof typeof editValues]
      }),
      {}
    );

  if (itemsView) {
    queriesData.items_view = itemsView;
  }

  const query = (concatSign: string) =>
    !Object.keys(queriesData).length
      ? ''
      : `${concatSign}${toQueryString(queriesData)}`;

  const baseUrl = isPashaLanding ? '/pasha' : '';
  return {
    href: `${baseUrl}/[...search]?url=${fromDataToUrl.join('/')}${query('&')}`.replace(
      /\/\//g,
      '/'
    ),
    as: `${baseUrl}/${fromDataToUrl.join('/')}${query('?')}`.replace(
      /\/\//g,
      '/'
    )
  };
};

const getPartOfFiltersObject = (result: QueryData) => ({
  roomIds: convertRoomsData(result['room_ids[]']),
  priceFrom:
    typeof result.price_from !== 'undefined'
      ? +result.price_from
      : result.price_from,
  priceTo:
    typeof result.price_to !== 'undefined' ? +result.price_to : result.price_to,
  areaFrom:
    typeof result.area_from !== 'undefined'
      ? +result.area_from
      : result.area_from,
  areaTo:
    typeof result.area_to !== 'undefined' ? +result.area_to : result.area_to,
  landAreaFrom:
    typeof result.land_area_from !== 'undefined'
      ? +result.land_area_from
      : result.land_area_from,
  landAreaTo:
    typeof result.land_area_to !== 'undefined'
      ? +result.land_area_to
      : result.land_area_to,
  buildingType: convertBuildingTypes(
    result['building_type[]'] || result.building_type
  )
});

export const fromUrlToObj = (
  data: { data: GetDataForTransformationsQuery },
  query: ParsedUrlQuery
) => {
  const { city, location, leased, category, room, slugFilter } =
    createUrlRegExps(data.data) as {
      city: string;
      location: string;
      leased: string;
      category: string;
      room: string;
      slugFilter: string;
    };
  const keys = [
    'cityId',
    'location_ids[]',
    'leased',
    'categoryId',
    'room_ids[]',
    'slugFilterValue'
  ];
  const regexp = new RegExp(
    `^${city}?\\/?${location}?\\/?${leased}?\\/?${category}?\\/?${room}?\\/?${slugFilter}?$`,
    'g'
  );
  const matchObj = regexp.exec(
    Array.isArray(query.search) ? query.search.join('/') : (query.search ?? '')
  );

  if (!matchObj) {
    return {
      noMatch: true as const
    };
  }
  const newArr = keys.map((item, index) => {
    const obj: Record<string, string | undefined> = {};
    obj[item] = matchObj[index + 1];
    return obj;
  });

  const search: Record<string, string | undefined> = {};

  for (let i = 0; i < newArr.length; i += 1) {
    search[Object.keys(newArr[i])[0]] = newArr[i][Object.keys(newArr[i])[0]];
  }

  const { slugFilterValue, ...searchWithoutSlugFilter } = search;
  type UrlToObjResult = QueryData & {
    cityId?: string;
    leased?: string;
    categoryId?: string;
  };
  const result = { ...searchWithoutSlugFilter, ...query } as UrlToObjResult;

  let matchingSlugConfig = null;

  if (slugFilterValue) {
    const slugConfigForValue = SLUG_FILTER_CONFIGS.find(c =>
      c.slugs.includes(slugFilterValue)
    );

    if (
      !slugConfigForValue ||
      result.leased !== slugConfigForValue.requiredDealSlug
    ) {
      return { noMatch: true as const };
    }

    matchingSlugConfig = slugConfigForValue;
  }

  const slugFilterResult = matchingSlugConfig
    ? matchingSlugConfig.slugToFilter(slugFilterValue)
    : {};

  const categoryId = getValueFromObject(
    data.data.categories,
    result.categoryId,
    'id'
  );

  const leasedRes =
    typeof result.leased === 'undefined'
      ? {}
      : { leased: result.leased === LeasedSlugs.RENT };

  const paidDailyFromQuery = result.paid_daily
    ? result.paid_daily === 'true'
    : undefined;

  return {
    filter: {
      cityId: getValueFromObject(data.data.cities, result.cityId, 'id'),
      categoryId,
      paidDaily: paidDailyFromQuery,
      hasBillOfSale: result.has_bill_of_sale
        ? result.has_bill_of_sale === 'true'
        : undefined,
      hasMortgage: result.has_mortgage
        ? result.has_mortgage === 'true'
        : undefined,
      hasInternalLoan: result.has_internal_loan
        ? result.has_internal_loan === 'true'
        : undefined,
      onlyResidences: result.only_residences
        ? result.only_residences === 'true'
        : undefined,
      hasRepair: result.has_repair ? result.has_repair === 'true' : undefined,
      floorFrom:
        typeof result.floor_from !== 'undefined'
          ? +result.floor_from
          : result.floor_from,
      floorTo:
        typeof result.floor_to !== 'undefined'
          ? +result.floor_to
          : result.floor_to,
      floorFirst: result.floor_first
        ? result.floor_first === 'true'
        : undefined,
      floorLast: result.floor_last ? result.floor_last === 'true' : undefined,
      locationIds: convertLocations(
        result['location_ids[]'],
        data.data.locations,
        result.cityId || 'baki'
      ),
      category_touched: Boolean(categoryId),
      ...getPartOfFiltersObject(result),
      ...leasedRes,
      ...slugFilterResult
    },
    sorting: getValueFromObject<ItemConnectionSort>(
      data.data.sort,
      result.sorting && result.sorting.replace(/\s/g, '+'),
      'name'
    )
  };
};

export const getInitialSortOption = (
  options: GetPashaFilterDataQuery | undefined,
  query: { sorting?: string } | null | undefined
) =>
  options?.sort.find(s =>
    query?.sorting
      ? s.params === query?.sorting
      : s.name === ItemSort.BumpedAtDesc
  );

export const getCurrentSortOption = (
  options: GetPashaFilterDataQuery | undefined,
  query: { sorting?: string } | null | undefined,
  sortOption: SortOption | null | undefined
) =>
  query?.sorting
    ? options?.sort.find(i => i.params === query.sorting)
    : sortOption;

export const PASHA_LANDING_SLUG = 'pasha_real_estate';

export const getInitialCategoryId = (categories: { id: string }[]) =>
  categories.some(category => category.id === '1') ? '1' : categories[0]?.id;

export const filterFromPashaUrlToObj = (
  data: { data: GetPashaFilterDataQuery },
  query: ParsedUrlQuery
) => {
  const { leased, category, room } = createUrlRegExps(data.data) as {
    leased: string;
    category: string;
    room: string;
  };
  const keys = ['leased', 'categoryId', 'room_ids[]'];
  const regexp = new RegExp(`^${leased}?\\/?${category}?\\/?${room}?$`, 'g');
  const matchObj = regexp.exec(
    Array.isArray(query.search) ? query.search.join('/') : (query.search ?? '')
  );
  const search: Record<string, string | undefined> = {};

  if (matchObj) {
    const newArr = keys.map((item, index) => {
      const obj: Record<string, string | undefined> = {};
      obj[item] = matchObj[index + 1];
      return obj;
    });
    for (let i = 0; i < newArr.length; i += 1) {
      search[Object.keys(newArr[i])[0]] = newArr[i][Object.keys(newArr[i])[0]];
    }
  }
  type PashaUrlResult = QueryData & { leased?: string; categoryId?: string };
  const result = { ...search, ...query } as PashaUrlResult;

  const categoryId = result.categoryId
    ? getValueFromObject(data.data.categories, result.categoryId, 'id')
    : getInitialCategoryId(data.data.categories);

  return {
    categoryId,
    ...getPartOfFiltersObject(result),
    leased:
      typeof result.leased === 'undefined' ? false : result.leased === 'kiraye',
    landingBrandSlug: PASHA_LANDING_SLUG
  };
};

export const adaptFiltersForState = (
  filters: FilterState | null | undefined
) => ({
  city_id: filters?.cityId,
  category_id: filters?.categoryId,
  leased: String(
    typeof filters?.leased === 'undefined' ? false : filters?.leased
  ),
  room_ids: filters?.roomIds,
  price_from: filters?.priceFrom,
  price_to: filters?.priceTo,
  area_from: filters?.areaFrom,
  area_to: filters?.areaTo,
  land_area_from: filters?.landAreaFrom,
  land_area_to: filters?.landAreaTo,
  paid_daily:
    // eslint-disable-next-line
    filters?.paidDaily != undefined ? [String(filters.paidDaily)] : undefined,
  has_bill_of_sale: filters?.hasBillOfSale,
  has_mortgage: filters?.hasMortgage,
  has_internal_loan: filters?.hasInternalLoan,
  has_repair:
    // eslint-disable-next-line
    filters?.hasRepair != undefined ? String(filters.hasRepair) : undefined,
  floor_from: filters?.floorFrom,
  floor_to: filters?.floorTo,
  floor_first:
    // eslint-disable-next-line
    filters?.floorFirst != undefined ? String(filters.floorFirst) : undefined,
  floor_last:
    // eslint-disable-next-line
    filters?.floorLast != undefined ? String(filters.floorLast) : undefined,
  location_ids: filters?.locationIds,
  building_type: filters?.buildingType,
  category_touched: filters?.category_touched,
  only_residences: filters?.onlyResidences
});

export const getSearchVippedItemsUrl = (
  filterState: Record<string, PropertyValue>
) => {
  const adaptedObj = Object.keys(filterState).reduce<
    Record<string, PropertyValue>
  >((acc, key) => {
    const snakeKey = camelToSnakeCase(key);

    if (filterState[key] !== undefined) {
      acc[snakeKey] = filterState[key];
    }
    return acc;
  }, {});

  return `/items/vipped?${toQueryString(adaptedObj)}`;
};

export const flattenCategories = (categories: Category[] = []): Category[] =>
  categories.flatMap(category =>
    Array.isArray(category.children) && category.children.length > 0
      ? flattenCategories(category.children)
      : [category]
  );

export const getCategoriesData = ({
  data,
  language
}: {
  data: { categories?: Category[] } | null | undefined;
  language: string;
}) => data?.categories || getCategoriesDefaultData(language);

export const getFlattenCategories = ({
  data,
  language
}: {
  data: Category[] | null | undefined;
  language: string;
}) => {
  const categories = getCategoriesData({
    data: { categories: data ?? undefined },
    language
  });

  return flattenCategories(categories as Category[] | undefined);
};
